import { Component, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { FileService } from '../../services/file.service';
import { ModalService } from '../../services/modal.service';

import { CmsPage, Region } from '../../models/content';
import { contentSlug, CONTENT_SLUG_PATTERN } from '../../content-slug';
import { pageBlockRequest } from './block-preview/block-preview-request';
import { BlockPreviewSettingsService } from './block-preview/block-preview-settings.service';
import { BlockPreviewSource, SectionsEditorSource } from './block-preview/block-preview-source';
import { ContentSectionsEditorComponent } from './content-sections-editor.component';
import { StagePreviewService } from './suburbs/workspace/stage-preview.service';

/**
 * Region editor — opened from ContentRegionListComponent. Returns the mutated
 * Region via dialogRef.close; the list writes it back + persists via
 * accountService.patchContent.
 *
 * Slug auto-generates from Name (contentSlug: mirrors picaluna-v3's slugify),
 * read-only until the author unlocks it to edit manually — the same pattern as
 * the locality / first-call-source / provider-type creators.
 */
/** What the list should do with the region's designed page after save. */
export type RegionPageAction =
  | { action: 'none' }
  | { action: 'remove'; id: string }
  | { action: 'upsert'; page: CmsPage };

@Component({
  selector: 'app-content-region',
  templateUrl: './content-region.component.html',
})
export class ContentRegionComponent implements OnInit, OnDestroy {
  region!: Region;
  isNew = false;
  form!: FormGroup;
  states = ['NSW', 'VIC', 'QLD', 'ACT', 'SA', 'WA', 'TAS', 'NT'];
  slugIsReadOnly = true;
  heroFileId: number | null = null;
  heroPreview: string | null = null;

  /**
   * The region IS its page (Ivan, 2026-08-21): one flat editor — identity
   * fields, then the hero + building blocks. Stored as a Pages record at the
   * hub address purely for pipeline reuse; nothing here exposes that. The
   * page's published state mirrors the region's; its title derives from the
   * name; its meta description IS the short description. Emptying the
   * sections removes the page (the automatic layout returns).
   */
  designedPage: CmsPage | null = null;
  /** True while the save request(s) are in flight — Save spins, Cancel locks. */
  saving = false;

  @ViewChild(ContentSectionsEditorComponent, { static: true })
  sectionsEditor!: ContentSectionsEditorComponent;

  /**
   * The block being edited, at the region page's address (#263): beside the form in the popup, on the stage when the
   * Suburbs tool mounts this editor in its side panel (too narrow for a column).
   */
  previewSource!: BlockPreviewSource;

  /** §9.2 promo panels — edited outside the form group, like list rows. */
  panels: Array<{
    /** Stable id — panel image filenames on the site derive from it. */
    id: string;
    heading: string;
    copy: string;
    imageFileId: number | null;
    imagePreview: string | null;
    linkLabel: string;
    linkTarget: string;
  }> = [];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public preview: BlockPreviewSettingsService,
    private fb: FormBuilder,
    private modalService: ModalService,
    private fileService: FileService,
    @Optional() public stage: StagePreviewService | null
  ) {}

  ngOnInit(): void {
    this.region = this.config.data.region;
    this.isNew = !!this.config.data.isNew;

    this.form = this.fb.group({
      name: new FormControl(this.region.name ?? '', [Validators.required]),
      slug: new FormControl(this.region.slug ?? '', [
        Validators.required,
        Validators.pattern(CONTENT_SLUG_PATTERN),
      ]),
      state: new FormControl(this.region.state ?? 'NSW', [Validators.required]),
      order: new FormControl(this.region.order ?? null),
      published: new FormControl(this.region.published !== false),
      intro: new FormControl(this.region.intro ?? ''),
      seoDescription: new FormControl(this.region.seoDescription ?? ''),
      body: new FormControl(this.region.body ?? ''),
    });

    const rawPanels: any = (this.region as any).promoPanels;
    this.panels = (Array.isArray(rawPanels) ? rawPanels : Object.values(rawPanels || {}))
      .filter((pn: any) => pn && typeof pn === 'object')
      .map((pn: any, i: number) => ({
        id: typeof pn.id === 'string' && pn.id ? pn.id : `p-${Math.random().toString(36).slice(2, 8)}-${i}`,
        heading: pn.heading || '',
        copy: pn.copy || '',
        imageFileId: typeof pn.imageFileId === 'number' ? pn.imageFileId : null,
        imagePreview: null,
        linkLabel: pn.link?.label || '',
        linkTarget: pn.link?.target || '',
      }));
    this.panels.forEach((pn) => {
      if (pn.imageFileId != null) {
        this.fileService.getPreviewLink(String(pn.imageFileId)).subscribe({
          next: (url) => (pn.imagePreview = url),
          error: () => {},
        });
      }
    });

    this.heroFileId = typeof this.region.heroFileId === 'number' ? this.region.heroFileId : null;
    if (this.heroFileId != null) {
      this.fileService.getPreviewLink(String(this.heroFileId)).subscribe({
        next: (url) => (this.heroPreview = url),
        error: () => {},
      });
    }

    // Start locked (auto) unless an existing slug already diverges from its name.
    this.slugIsReadOnly = (this.region.slug || '') === contentSlug(this.region.name || '');

    const dp: CmsPage | undefined = this.config.data.designedPage;
    this.designedPage = dp ? JSON.parse(JSON.stringify(dp)) : null;
    this.sectionsEditor.initFrom(this.designedPage?.sections);
    this.previewSource = new SectionsEditorSource(this.sectionsEditor, (sections, active) => {
      const { state, slug } = this.form.getRawValue();
      return pageBlockRequest(hubPath(state, contentSlug(slug || '')), sections, active);
    });
    this.stage?.show(this.previewSource);
  }

  ngOnDestroy(): void {
    this.stage?.release(this.previewSource);
  }

  /** As the name is typed, auto-fill the slug while it's locked. */
  onNameInput(): void {
    if (this.slugIsReadOnly) {
      this.form.get('slug')!.setValue(contentSlug(this.form.get('name')!.value || ''));
    }
  }

  toggleSlugEdit(): void {
    this.slugIsReadOnly = !this.slugIsReadOnly;
    // Re-locking re-syncs the slug from the current name.
    if (this.slugIsReadOnly) {
      this.form.get('slug')!.setValue(contentSlug(this.form.get('name')!.value || ''));
    }
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value = { ...this.form.value };
    // Final normalise so a manual slug edit is always URL-safe.
    value.slug = contentSlug(value.slug || '');
    // <input type="number"> yields strings — coerce so the EAV stores a number.
    value.order = value.order === null || value.order === '' ? null : Number(value.order);
    value.intro = (value.intro || '').trim() || null;
    value.seoDescription = (value.seoDescription || '').trim() || null;
    value.body = (value.body || '').trim() || null;
    // ── The page half of the save ────────────────────────────────────────
    // Sections present → upsert the page (published with the region, titled
    // from the name, described by the short description). Sections emptied →
    // remove it (the automatic layout returns). No extra switches.
    const regionPublished = value.published !== false;
    const pagePath = hubPath(value.state, value.slug);
    let pageAction: RegionPageAction = { action: 'none' };
    if (this.sectionsEditor.sections.length === 0) {
      if (this.designedPage) pageAction = { action: 'remove', id: this.designedPage._meta.id };
    } else {
      const problem = this.sectionsEditor.validate(regionPublished);
      if (problem) {
        this.modalService.generic({
          title: "The page can't be saved yet",
          copy: [problem],
          buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
        });
        return;
      }
      const prevPath = (this.designedPage?.path || '').trim().replace(/\/+$/, '');
      const redirectFrom = new Set((this.designedPage?.redirectFrom ?? []).filter((r) => r !== pagePath));
      // A slug/state change moves the hub URL — the page moves with it and
      // the old address 301s, instead of orphaning into an invalid path.
      if (prevPath && prevPath !== pagePath) redirectFrom.add(prevPath);
      pageAction = {
        action: 'upsert',
        page: {
          _meta: this.designedPage?._meta ?? { id: `id-page-${Math.random().toString(36).slice(2, 10)}`, index: 0 },
          ...(this.designedPage ?? {}),
          path: pagePath,
          title: `Funerals in ${value.name} | Picaluna`,
          description: (value.seoDescription || '').trim() || (value.intro || '').trim() || undefined,
          published: regionPublished,
          ...(redirectFrom.size ? { redirectFrom: [...redirectFrom] } : {}),
          sections: this.sectionsEditor.toMap(),
        } as CmsPage,
      };
    }

    value.heroFileId = this.heroFileId;
    // Panels: drop empty rows; a link needs BOTH halves or it is dropped —
    // a label without a target renders as dead text on the site.
    value.promoPanels = this.panels
      .filter((pn) => pn.heading.trim())
      .map((pn) => ({
        id: pn.id,
        heading: pn.heading.trim(),
        ...(pn.copy.trim() ? { copy: pn.copy.trim() } : {}),
        ...(typeof pn.imageFileId === 'number' ? { imageFileId: pn.imageFileId } : {}),
        ...(pn.linkLabel.trim() && pn.linkTarget.trim()
          ? { link: { label: pn.linkLabel.trim(), target: pn.linkTarget.trim() } }
          : {}),
      }));
    Object.assign(this.region, value);
    // Save-then-close: dialog open (Save spinning) until the region write AND
    // the page write have both landed — Publish can't race them.
    const save = this.config.data.save as
      | ((r: { region: Region; pageAction: RegionPageAction }) => any)
      | undefined;
    if (!save) {
      this.ref.close({ saved: true, region: this.region, pageAction });
      return;
    }
    this.saving = true;
    save({ region: this.region, pageAction }).subscribe({
      next: () => this.ref.close({ saved: true, region: this.region, pageAction, persisted: true }),
      error: (err: any) => {
        this.saving = false;
        this.modalService.error(err?.error?.error || err);
      },
    });
  }

  uploadHero(): void {
    const ref = this.modalService.fileUpload({
      title: 'Upload region hero image',
      bucketPrefix: 'region-heroes',
      // Raster only — the site's asset globs are {jpg,jpeg,png,webp,avif}, so a
      // GIF or SVG uploads and syncs but silently renders nothing.
      accept: 'image/jpeg,image/png,image/webp,image/avif',
      multiple: false,
    });
    ref.onClose.subscribe((result?: { files: { id: number; link?: string }[] }) => {
      const file = result?.files?.[0];
      if (!file) return;
      this.heroFileId = file.id;
      if (file.link) this.heroPreview = file.link;
    });
  }

  removeHero(): void {
    this.heroFileId = null;
    this.heroPreview = null;
  }

  addPanel(): void {
    this.panels.push({
      id: `p-${Math.random().toString(36).slice(2, 8)}`,
      heading: '', copy: '', imageFileId: null, imagePreview: null, linkLabel: '', linkTarget: '',
    });
  }
  removePanel(i: number): void {
    this.panels.splice(i, 1);
  }
  movePanel(i: number, delta: number): void {
    const j = i + delta;
    if (j < 0 || j >= this.panels.length) return;
    const [x] = this.panels.splice(i, 1);
    this.panels.splice(j, 0, x);
  }
  uploadPanelImage(pn: { imageFileId: number | null; imagePreview: string | null }): void {
    const ref = this.modalService.fileUpload({
      title: 'Upload panel image',
      bucketPrefix: 'region-panels',
      // Raster only — the site's asset globs are {jpg,jpeg,png,webp,avif}, so a
      // GIF or SVG uploads and syncs but silently renders nothing.
      accept: 'image/jpeg,image/png,image/webp,image/avif',
      multiple: false,
    });
    ref.onClose.subscribe((result?: { files: { id: number; link?: string }[] }) => {
      const file = result?.files?.[0];
      if (!file) return;
      pn.imageFileId = file.id;
      if (file.link) pn.imagePreview = file.link;
    });
  }
  removePanelImage(pn: { imageFileId: number | null; imagePreview: string | null }): void {
    pn.imageFileId = null;
    pn.imagePreview = null;
  }

  onCancel(): void {
    this.ref.close({ saved: false });
  }
}

/** The region's page: its hub address (picaluna-v3 /{state}/{region}). */
function hubPath(state: string | null | undefined, slug: string): string {
  return `/${String(state || 'nsw').toLowerCase()}/${slug}`;
}
